import { cn } from '@/utils/styles';
import { liveUpdateAttrs } from '@/libs/contentful/utils';

import CTA from '@/components/shared/CTA/CTA';
import Media from '@/components/shared/Media/Media';
import RichTextList from '@/components/shared/RichTextList/RichTextList';
import RichTextHeadline from '@/components/shared/RichTextHeadline/RichTextHeadline';

import { CardsVerticalScrollItemDesktopProps } from './types';
import { getBulletListStyle } from '@/utils/bulletListHelpers';

/** Desktop card content (headline, copy, CTA) for the left column */
export const CardsVerticalScrollItemDesktopContent = ({
  card,
  index,
  isActive,
  cardRef,
  onClick,
  bulletStyle,
  activeColorway,
}: CardsVerticalScrollItemDesktopProps) => {
  const headlineContent = (
    <div
      className={cn('font-semibold text-heading-lg text-[2rem] ml:text-5xl text-left')}
      {...liveUpdateAttrs(card.sys?.id, 'headline')}>
      <RichTextHeadline headline={card.headline} />
    </div>
  );

  const buttonContent = (
    <button
      type="button"
      onClick={onClick}
      className="w-full cursor-pointer hover:opacity-60 transition-opacity bg-transparent border-none p-0">
      {headlineContent}
    </button>
  );

  return (
    <div
      ref={cardRef}
      className={cn('transition-all duration-300', isActive ? 'opacity-100' : 'opacity-20', activeColorway)}
      data-testid={`cards-vertical-scroll-item-${index}`}>
      {isActive ? headlineContent : buttonContent}

      {/* Expanded Content */}
      {isActive && (
        <div className="animate-fadeIn mb-8" data-testid={`cards-vertical-scroll-item-${index}-content`}>
          <div
            className="font-medium text-body-serif font-serif text-base ml:text-lg mb-6 mt-4"
            {...liveUpdateAttrs(card.sys?.id, 'copy')}>
            <RichTextList item={card.copy} listItemClasses="my-0" bulletListStyle={getBulletListStyle(bulletStyle)} />
          </div>
          {card.cta && <CTA cta={card.cta} variant="outline" />}
        </div>
      )}
    </div>
  );
};

/** Desktop card media for the right column */
export const CardsVerticalScrollItemDesktopMedia = ({
  card,
  isActive,
}: Pick<CardsVerticalScrollItemDesktopProps, 'card' | 'isActive'>) => {
  // enforce the aspect ratio while still keeping it constrained to the parent container
  const ratioValue = card.desktopAspectRatio?.value ?? 1; // Default to 1:1 aspect ratio
  const style =
    ratioValue < 1
      ? { width: '100%', height: `${ratioValue * 100}%` }
      : { width: `${(1 / ratioValue) * 100}%`, height: '100%' };

  return (
    <div
      className={cn(
        'grid transition-opacity duration-500 absolute right-0',
        isActive ? 'opacity-100' : 'opacity-0 pointer-events-none',
      )}
      style={style}>
      {card.desktopMedia && (
        <Media
          media={{ ...card.desktopMedia, fill: true }}
          aspectRatio={{ value: ratioValue }}
          sizes="596px"
          className="rounded-3xl col-start-1 row-start-1"
        />
      )}
    </div>
  );
};
